/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable @typescript-eslint/indent */
import {
  Appointment,
  AppointmentProduct,
  AppointmentType,
  Clinic,
  graphql,
  Lab,
  Maybe,
  TimeSlot,
  utils
} from 'kb-shared';
import gql from 'graphql-tag';
import Honeybadger from '@honeybadger-io/js';
import { History } from 'history';
import qs from 'qs';
const { momentInTimeZone } = utils;

const client = graphql.client;

export type StepType =
  | 'select_appointment'
  | 'location'
  | 'time'
  | 'create_account'
  | 'confirm'
  | 'success';

export type Step = {
  type: StepType;
  name: string;
};

export type Membership = {
  id: string;
  name: string;
  description: string;
  rateCents: number;
};

export type PurchasedProduct =
  | {
      type: 'appointment';
      data: Appointment;
    }
  | { type: 'membership'; data: Membership }
  | { type: 'enterprise_membership'; data: {} };

const CreateAccountStep: Step = {
  type: 'create_account',
  name: 'Create Account'
};

const DefaultSteps = {
  select_appointment: {
    type: 'select_appointment',
    name: 'Services'
  },
  location: {
    type: 'location',
    name: 'Location'
  },
  create_account: CreateAccountStep,
  confirm: {
    type: 'confirm',
    name: 'Confirm'
  },
  success: {
    type: 'success',
    name: ''
  }
};

const LoggedInSteps = {
  select_appointment: {
    type: 'select_appointment',
    name: 'Services'
  },
  location: {
    type: 'location',
    name: 'Location'
  },
  confirm: {
    type: 'confirm',
    name: 'Confirm'
  },
  success: {
    type: 'success',
    name: ''
  }
};

export type Data = {
  steps: { [key in StepType]?: Step };
  selectedStep: StepType;
  selectedProduct: AppointmentProduct<AppointmentType, Membership, Appointment> | null;
  selectedLab: Lab | null;
  selectedClinic: Clinic | null;
  selectedWeekString: string | null;
  selectedTimeSlot: TimeSlot | null;
  selectedAppointmentTypes: Array<AppointmentType> | null;
  purchasedProduct: PurchasedProduct | null;
  partnerClinicSearch: string | null;
  target: Maybe<string>;
  requireUniqueId?: string | null;
};

type URLParams = {
  step: StepType;
  appointment_id: string | null;
  appointment_types: string | null;
  membership_id: string | null;
  lab_id: string | null;
  clinic_id: string | null;
  week_string: string | null;
  time_slot_id: string | null;
  partner_clinic_search: string | null;
  reschedule_appointment_id: string | null;
  confirmed_appointment_id: string | null;
  confirmed_membership_id: string | null;
  enterprise_membership: boolean | null;
  require_unique_id: string | null;
  // If a redirect should happen after login/signup
  target: Maybe<string>;
};

//TODO: Import fragments from each step and use them here
const QUERY_APPOINTMENT_TYPE = gql`
  query appointmentType($appointmentId: Int!) {
    appointmentType(appointmentTypeId: $appointmentId) {
      id
      name
      virtual
      virtualFert
      description
      videoLink
      rateCents
      category {
        id
        name
      }
    }
  }
`;
const QUERY_APPOINTMENT_TYPES = gql`
  query appointmentTypes($appointmentTypeIds: [Int!]) {
    appointmentTypes(ids: $appointmentTypeIds) {
      id
      name
      virtual
      virtualFert
      description
      videoLink
      rateCents
      category {
        id
        name
      }
    }
  }
`;

const QUERY_LAB = gql`
  query lab($labId: Int!) {
    lab(labId: $labId) {
      id
      name
      stripePublicKey
    }
  }
`;

const QUERY_LABS = gql`
  query labs {
    labs {
      id
      name
      stripePublicKey
    }
  }
`;

const QUERY_LOCATION = gql`
  query location($locationId: Int!) {
    location(locationId: $locationId) {
      id
      name
      address
      timeZone
      imageUrl
      patientFacing
    }
  }
`;

const QUERY_CLINICS = gql`
  query clinics($appointmentTypeId: Int!, $labId: Int!) {
    clinics: locations(appointmentTypeId: $appointmentTypeId, labId: $labId) {
      id
      name
      address
      timeZone
      imageUrl
      patientFacing
    }
  }
`;

const QUERY_TIME_SLOT = gql`
  query timeSlot($timeSlotId: Int!) {
    timeslot(timeslotId: $timeSlotId) {
      id
      videoLink
      location {
        id
        name
        address
        timeZone
      }
      lab {
        stripePublicKey
      }
      startTime
      endTime
      timeZone
    }
  }
`;

const QUERY_MEMBERSHIP = gql`
  query membership($membershipId: Int!) {
    membership(membershipId: $membershipId) {
      description
      id
      name
      employer
      rateCents
    }
  }
`;

const QUERY_BOOKED_APPOINTMENT = gql`
  query appointment($appointmentId: Int!) {
    appointment(appointmentId: $appointmentId) {
      id
      videoLink
      location {
        id
        address
        name
        patientFacing
        timeZone
      }
      startTime
      endTime
      timeZone
      cancelable
      reschedulable
      appointmentType {
        id
        virtual
        virtualFert
        name
        description
        videoLink
        rateCents
        category {
          id
          name
        }
      }
    }
  }
`;

const INITIAL_DATA_STATE = {
  steps: DefaultSteps,
  selectedStep: '',
  selectedProduct: null,
  selectedLab: null,
  selectedWeekString: null,
  selectedTimeSlot: null,
  selectedAppointmentTypes: null,
  partnerClinicSearch: null,
  selectedClinic: null,
  purchasedProduct: null
};

export default class BookingStateManager {
  _userInitiallyLoggedIn: boolean;
  history: History;
  onDataChange: (data: Data) => void;
  dataCache: {
    [urlParamString: string]: Data;
  };
  // @ts-ignore
  urlParams: URLParams;
  // @ts-ignore
  data: Data = INITIAL_DATA_STATE;

  urlParms: URLParams = {
    step: 'select_appointment',
    appointment_id: null,
    appointment_types: null,
    membership_id: null,
    lab_id: null,
    clinic_id: null,
    week_string: null,
    time_slot_id: null,
    partner_clinic_search: null,
    reschedule_appointment_id: null,
    confirmed_appointment_id: null,
    confirmed_membership_id: null,
    target: null,
    enterprise_membership: null,
    require_unique_id: null
  };

  constructor(history: History, onDataChange: (data: Data) => void, isLoggedIn: boolean) {
    if (isLoggedIn) {
      this.data = {
        ...this.data,
        // @ts-ignore
        steps: LoggedInSteps
      };
    }

    this._userInitiallyLoggedIn = isLoggedIn;
    this.onDataChange = onDataChange;
    // Set initial state of data cache to use initial state of data for empty url params
    this.dataCache = { '': this.data };
    this.history = history;

    // Initialize and check for existing URL params
    const searchQuery = history.location.search;
    if (searchQuery) {
      this.deriveDataFromUrl(searchQuery, { initial: true, isLoggedIn })
        .then(data => {
          this.data = data;
          this.onDataChange(this.data);
        })
        .catch(error => Honeybadger.notify(error));
    } else {
      this.data.selectedStep = 'select_appointment';
    }

    // @ts-ignore
    history.listen((location, action) => {
      if (action === 'POP') {
        this.deriveDataFromUrl(location.search)
          .then(data => {
            this.data = data;
            this.onDataChange(this.data);
          })
          .catch(error => Honeybadger.notify(error));
      }
    });
  }

  resetState() {
    this.dataCache = { '': this.data };
    // @ts-ignore
    this.data = INITIAL_DATA_STATE;
    this.onDataChange(this.data);
  }

  generateConfirmationLink(): string | null {
    const { purchasedProduct } = this.data;
    if (!purchasedProduct) {
      return null;
    }
    switch (purchasedProduct.type) {
      case 'appointment':
        return `/confirmation?step=success&confirmed_appointment_id=${purchasedProduct.data.id}`;
      case 'membership':
        // Use the actual id of the membership product, not the id of the membership record for this user.
        const membershipProductId =
          // @ts-ignore
          (this.data.selectedProduct && this.data.selectedProduct.data.id) || 0;
        return `/confirmation?step=success&confirmed_membership_id=${membershipProductId}`;
      case 'enterprise_membership':
        return `/confirmation?step=success&enterprise_membership=true`;
      default:
        return null;
    }
  }

  deriveURLParams(data: Data): URLParams {
    const {
      selectedProduct,
      selectedAppointmentTypes,
      selectedLab,
      selectedClinic,
      selectedWeekString,
      selectedTimeSlot,
      purchasedProduct,
      partnerClinicSearch
    } = this.data;

    const appointmentId =
      (selectedProduct && selectedProduct.type === 'appointment' && selectedProduct.data.id) ||
      null;

    const membershipId =
      (selectedProduct && selectedProduct.type === 'membership' && selectedProduct.data.id) || null;

    const rescheduleAppointmentId =
      (selectedProduct &&
        selectedProduct.type === 'reschedule_appointment' &&
        selectedProduct.data.id) ||
      null;

    const confirmedAppointmentId =
      (purchasedProduct && purchasedProduct.type === 'appointment' && purchasedProduct.data.id) ||
      null;

    const confirmedMembershipId =
      (purchasedProduct && purchasedProduct.type === 'membership' && purchasedProduct.data.id) ||
      null;

    const enterpriseMembership =
      (selectedProduct && selectedProduct.type === 'enterprise_membership') || null;

    return {
      step: data.selectedStep,
      appointment_types:
        selectedAppointmentTypes && selectedAppointmentTypes.map(apptType => apptType.id).join(','),
      appointment_id: appointmentId,
      membership_id: membershipId,
      lab_id: selectedLab && selectedLab.id,
      // @ts-ignore
      clinic_id: selectedClinic && selectedClinic.id,
      week_string: selectedWeekString,
      time_slot_id: selectedTimeSlot && selectedTimeSlot.id,
      reschedule_appointment_id: rescheduleAppointmentId,
      partner_clinic_search: partnerClinicSearch,
      confirmed_appointment_id: confirmedAppointmentId,
      confirmed_membership_id: confirmedMembershipId,
      enterprise_membership: enterpriseMembership
    };
  }

  deriveDataFromUrl(
    url: string,
    options: { initial: boolean; isLoggedIn: boolean } = {
      initial: false,
      isLoggedIn: false
    }
  ): Promise<Data> {
    const params = qs.parse(url, { ignoreQueryPrefix: true }) as any;
    // Check Cache first to see if we have stored this state already
    // This occurs if user is hitting back/forward in browser
    const urlWithoutQueryPrefix = qs.stringify(params, { addQueryPrefix: false });
    const cachedData: Data | null = this.dataCache[urlWithoutQueryPrefix];

    if (cachedData) {
      return Promise.resolve(cachedData);
    }
    // If there is no cache, we need to load all data manually
    return this.deriveData(params, options);
  }

  deriveData(
    params: URLParams,
    options: { initial: boolean; isLoggedIn: boolean } = {
      initial: false,
      isLoggedIn: false
    }
  ): Promise<Data> {
    /**
     * Construct data variable that will be resolved/returned
     * selectedStep and selectedWeekString can be pulled directly from url.
     * Other params (if they exist) are used to load data from the server in order
     * to populate the loadedData. Eg if we have an appointment_id, we load the appointment
     * from the server so we have it in our local data.
     */
    const preSelectedStep =
      !options.isLoggedIn && params.step === 'confirm' ? 'create_account' : params.step;
    const loadedData: Data = {
      ...this.data,
      ...(preSelectedStep ? { selectedStep: preSelectedStep } : null),
      ...(params.week_string ? { selectedWeekString: params.week_string } : null),
      ...(params.target ? { target: params.target } : null),
      partnerClinicSearch: params.partner_clinic_search,
      requireUniqueId: params.require_unique_id
    };

    // If this is initial page load and we have specified the create_account step, do not show other steps
    if (options.initial && params.step && params.step === 'create_account') {
      loadedData.steps = { create_account: CreateAccountStep };
    }

    const variables = {
      appointmentId: params.appointment_id && parseInt(params.appointment_id),
      appointmentTypeIds:
        params.appointment_types && params.appointment_types.split(',').map(id => parseInt(id)),
      membershipId: params.membership_id && parseInt(params.membership_id),
      labId: params.lab_id && parseInt(params.lab_id),
      clinicId: params.clinic_id && parseInt(params.clinic_id),
      timeSlotId: params.time_slot_id && parseInt(params.time_slot_id),
      rescheduleAppointmentId:
        params.reschedule_appointment_id && parseInt(params.reschedule_appointment_id),
      confirmedAppointmentId:
        params.confirmed_appointment_id && parseInt(params.confirmed_appointment_id),
      confirmedMembershipId:
        params.confirmed_membership_id && parseInt(params.confirmed_membership_id),
      enterpriseMembership: params.enterprise_membership,
      partnerClinicId:
        params.partner_clinic_search != 'search' ? params.partner_clinic_search : null
    };

    // Construct queries for each possible data to be loaded.
    // If the id does not exist, resolve an empty promise so that we can conveniently
    // just pass all promises into the Promise.all without doing null checks on them.
    const loadAppointment: Promise<AppointmentType | undefined> = variables.appointmentId
      ? client
          .query({
            query: QUERY_APPOINTMENT_TYPE,
            variables: { appointmentId: variables.appointmentId }
          })
          .then((response: any) => {
            loadedData.selectedProduct = {
              type: 'appointment',
              data: response.data.appointmentType
            };
            return response.data.appointmentType;
          })
          .catch(error => Honeybadger.notify(error))
      : Promise.resolve();

    const loadAppointmentTypes = variables.appointmentTypeIds
      ? client
          .query({
            query: QUERY_APPOINTMENT_TYPES,
            variables: { appointmentTypeIds: variables.appointmentTypeIds }
          })
          // @ts-ignore
          .then(response => {
            loadedData.selectedAppointmentTypes = response.data.appointmentTypes;
          })
          .catch(error => Honeybadger.notify(error))
      : Promise.resolve();

    // Note that at current, if appointment_id and membership_id are both passed into
    // the url, it is a race condition on which one will load first
    // and overwrite the other in `loadedData.selectedProduct`
    // This isn't a supported use case at the moment to supply a membership_id and
    // an appointment_id
    const loadMembership = variables.membershipId
      ? client
          .query({
            query: QUERY_MEMBERSHIP,
            variables: { membershipId: variables.membershipId }
          })
          // @ts-ignore
          .then(response => {
            loadedData.selectedProduct = {
              type: 'membership',
              data: response.data.membership
            };
          })
          .catch(error => Honeybadger.notify(error))
      : Promise.resolve();

    const loadLab = variables.labId
      ? client
          .query({
            query: QUERY_LAB,
            variables: { labId: variables.labId }
          })
          // @ts-ignore
          .then(response => {
            loadedData.selectedLab = response.data.lab;
          })
          .catch(error => Honeybadger.notify(error))
      : client
          .query({ query: QUERY_LABS })
          .then((response: any) => {
            if (response.data.labs.length === 1) {
              loadedData.selectedLab = response.data.labs[0];
            }
          })
          .catch(error => Honeybadger.notify(error));

    const loadLocation = variables.clinicId
      ? client
          .query({
            query: QUERY_LOCATION,
            variables: { locationId: variables.clinicId }
          })
          // @ts-ignore
          .then(response => {
            loadedData.selectedClinic = response.data.location;
          })
          .catch(error => Honeybadger.notify(error))
      : Promise.resolve();

    const loadTimeSlot = variables.timeSlotId
      ? client
          .query({
            query: QUERY_TIME_SLOT,
            variables: { timeSlotId: variables.timeSlotId }
          })
          // @ts-ignore
          .then(response => {
            loadedData.selectedTimeSlot = response.data.timeslot;
          })
          .catch(error => Honeybadger.notify(error))
      : Promise.resolve();

    const loadRescheduleAppointment = variables.rescheduleAppointmentId
      ? client
          .query({
            query: QUERY_BOOKED_APPOINTMENT,
            variables: {
              appointmentId: variables.rescheduleAppointmentId
            }
          })
          // @ts-ignore
          .then(response => {
            const appointmentToReschedule = response.data.appointment;
            loadedData.selectedProduct = {
              type: 'reschedule_appointment',
              data: appointmentToReschedule
            };
          })
          .catch(error => Honeybadger.notify(error))
      : Promise.resolve();

    const loadConfirmedAppointment = variables.confirmedAppointmentId
      ? client
          .query({
            query: QUERY_BOOKED_APPOINTMENT,
            variables: {
              appointmentId: variables.confirmedAppointmentId
            }
          })
          // @ts-ignore
          .then(response => {
            const bookedAppointment = response.data.appointment;
            loadedData.purchasedProduct = {
              type: 'appointment',
              data: bookedAppointment
            };
          })
          .catch(error => Honeybadger.notify(error))
      : Promise.resolve();

    const loadConfirmedMembership = variables.confirmedMembershipId
      ? client
          .query({
            query: QUERY_MEMBERSHIP,
            variables: { membershipId: variables.confirmedMembershipId }
          })
          // @ts-ignore
          .then(response => {
            loadedData.purchasedProduct = {
              type: 'membership',
              data: response.data.membership
            };
          })
          .catch(error => Honeybadger.notify(error))
      : Promise.resolve();

    const loadEnterpriseMembership = Promise.resolve();
    if (variables.enterpriseMembership) {
      loadedData.selectedProduct = {
        type: 'enterprise_membership',
        data: {}
      };
      loadedData.purchasedProduct = {
        type: 'enterprise_membership',
        data: {}
      };
      const loadEnterpriseMembership = Promise.resolve(loadedData);
    }

    return Promise.all([
      loadAppointment,
      loadAppointmentTypes,
      loadMembership,
      loadLab,
      loadLocation,
      loadTimeSlot,
      loadRescheduleAppointment,
      loadConfirmedAppointment,
      loadConfirmedMembership,
      loadEnterpriseMembership
    ]).then(async ([loadedAppointment]) => {
      const appointmentTypes = ([] as AppointmentType[])
        .concat(loadedData.selectedAppointmentTypes || [])
        .concat(loadedAppointment || []);

      const virtualApptType = appointmentTypes.find(apptType => apptType.virtual);
      const inPersonApptType = appointmentTypes.find(apptType => !apptType.virtual);
      const isOnlyVirtual = virtualApptType && !inPersonApptType;

      if (isOnlyVirtual) {
        loadedData.selectedProduct = loadedData.selectedProduct ?? {
          type: 'appointment',
          data: virtualApptType as AppointmentType
        };
      }

      // we only pre-select a clinic if we have a selected lab (aka market), appointment
      // and there is no virtual options to select
      if (
        loadedData.selectedLab &&
        loadedData.selectedProduct?.type === 'appointment' &&
        !isOnlyVirtual
      ) {
        const { data } = await client.query({
          query: QUERY_CLINICS,
          variables: {
            labId: parseInt(loadedData.selectedLab.id),
            appointmentTypeId: parseInt(loadedData.selectedProduct.data.id)
          }
        });

        if (data?.clinics.length === 1) {
          loadedData.selectedClinic = data.clinics[0];
        }
      }

      // we only skip the location step if there is only virtual appointments
      // or the clinic (aka location) is already selected
      if (loadedData.selectedStep === 'location' && (isOnlyVirtual || loadedData.selectedClinic)) {
        loadedData.selectedStep = 'time';
      }

      if (!this.data.selectedStep) {
        this.data.selectedStep = 'select_appointment';
      }

      return loadedData;
    });
  }

  getUrlParamsString(addQueryPrefix: boolean): string {
    const paramsString = qs.stringify(this.urlParams, {
      addQueryPrefix: addQueryPrefix,
      skipNulls: true
    });
    return paramsString;
  }

  // @ts-ignore
  updateDataAndUrl(data: Data, options?: { replace: boolean } = { replace: false }) {
    this.data = data;
    this.urlParams = this.deriveURLParams(this.data);
    const paramsString = this.getUrlParamsString(true);
    if (options.replace) {
      this.history.replace(paramsString);
    } else {
      this.history.push(paramsString);
    }
    // Push the date state for this paramsString into the cache. Regenerate it without the queryPrefix
    const paramsStringWithoutQueryPrefix = this.getUrlParamsString(false);
    this.dataCache[paramsStringWithoutQueryPrefix] = data;
    this.onDataChange(this.data);
  }

  stepAtIndex(index: number): Step {
    const keyForIndex = Object.keys(this.data.steps)[index];
    // @ts-ignore
    return this.data.steps[keyForIndex];
  }

  goBacktoStep(step: StepType) {
    /**
     * Depending on the step, clear the data that is in any step after this one.
     * This is to avoid storing bad data that may not match.
     * ie. if a user originally selected a fertility assessment, a location, a time slot, a lab
     * gets all the way to confirmation, but then decides to go back to "Select Appointment"
     * where they choose a video consulatation instead, we need to clear the ttime slot, the lab, the location, etc
     */
    switch (step) {
      case 'select_appointment':
        this.updateDataAndUrl({
          ...this.data,
          selectedStep: step,
          selectedProduct: null,
          selectedLab: null,
          selectedWeekString: null,
          selectedTimeSlot: null,
          selectedClinic: null
        });
        break;
      case 'location':
        this.updateDataAndUrl({
          ...this.data,
          selectedStep: step
        });
        break;
      case 'time':
        this.updateDataAndUrl({
          ...this.data,
          selectedStep: step
        });
        break;
      case 'create_account':
        this.updateDataAndUrl({
          ...this.data,
          selectedStep: step
        });
        break;
      case 'confirm':
        break;
      default:
    }
  }

  setAppointment(appointment: AppointmentType) {
    if (window.analytics) {
      window.analytics.track('Appointment Type Selected');
    }

    this.updateDataAndUrl({
      ...this.data,
      selectedProduct: { type: 'appointment', data: appointment },
      selectedStep: appointment.virtual ? 'time' : 'location'
    });
  }

  setPartnerClinic() {
    if (window.analytics) {
      window.analytics.track('Partner Clinic Search Started');
    }

    this.updateDataAndUrl({
      ...this.data,
      partnerClinicSearch: 'search'
    });
  }

  setSuccessPartnerClinic() {
    this.updateDataAndUrl({
      ...this.data,
      selectedStep: this._userInitiallyLoggedIn ? 'success' : 'create_account'
    });
  }

  addMembershipToCart(membership: Membership) {
    if (window.analytics) {
      window.analytics.track('Membership Selected');
    }

    this.updateDataAndUrl({
      ...this.data,
      selectedProduct: { type: 'membership', data: membership },
      // If user is logged in, go to confirm. Otherwise go to create_account
      selectedStep: this._userInitiallyLoggedIn ? 'confirm' : 'create_account'
    });
  }

  setLab(lab: Lab | null) {
    if (window.analytics) {
      window.analytics.track('Lab Selected', {
        lab_id: lab?.id
      });
    }
    this.updateDataAndUrl(
      {
        ...this.data,
        selectedLab: lab,
        // Clear other fields if lab changes
        selectedClinic: null,
        selectedTimeSlot: null,
        selectedWeekString: null
      },
      { replace: true }
    );
  }

  setVirtual(appointmentType: AppointmentType | undefined) {
    if (!appointmentType) {
      return;
    }
    if (window.analytics) {
      window.analytics.track('Virtual Selected');
    }
    this.updateDataAndUrl({
      ...this.data,
      selectedClinic: null,
      selectedStep: 'time',
      selectedProduct: { type: 'appointment', data: appointmentType }
    });
  }

  setClinic(clinic: Clinic | null, appointmentType: AppointmentType | undefined = undefined) {
    if (window.analytics) {
      window.analytics.track('Clinic Selected', {
        clinic_name: clinic?.name,
        clinic_id: clinic?.id
      });
    }
    this.updateDataAndUrl(
      {
        ...this.data,
        selectedClinic: clinic,
        selectedWeekString: null,
        selectedStep: 'time',
        selectedProduct: appointmentType
          ? { type: 'appointment', data: appointmentType }
          : this.data.selectedProduct
      },
      { replace: false }
    );
  }

  setWeekString(weekString: string | null) {
    this.updateDataAndUrl(
      {
        ...this.data,
        selectedWeekString: weekString
      },
      { replace: true }
    );
  }

  setTimeSlot(slot: TimeSlot | null) {
    if (window.analytics) {
      const formattedTime =
        slot?.startTime &&
        // @ts-ignore
        momentInTimeZone(slot.startTime, slot.timeZone).format('MM-DD-YYYY HH:mm z');
      window.analytics.track('Time Slot Selected', {
        time_slot_id: slot?.id,
        time_slot_time: formattedTime || ''
      });
    }

    const nextStep: StepType = this._userInitiallyLoggedIn ? 'confirm' : 'create_account';
    this.updateDataAndUrl({
      ...this.data,
      selectedTimeSlot: slot,
      selectedStep: nextStep
    });
  }

  canMoveToStep(step: StepType): boolean {
    // Attempt to find the step inside of data.steps
    return !!this.data.steps[step];
  }

  setUserDidSignUp() {
    if (this.canMoveToStep('confirm')) {
      if (window.analytics) {
        window.analytics.page('Confirm Purchase');
      }

      this.updateDataAndUrl({
        ...this.data,
        selectedStep: 'confirm'
      });
    } else {
      // If only step available is create_account, then user
      // has skipped booking and just signed up. Bring them to login after verification
      const allSteps = Object.keys(this.data.steps);
      if (allSteps.length === 1 && allSteps[0] === 'create_account') {
        let url = '/login';
        if (this.data.target) {
          url = url.concat(`?target=${this.data.target}`);
        }
        this.history.push(url);
      }
    }
  }

  setBookingAppointmentSuccess(appointment: Appointment) {
    if (window.analytics) {
      window.analytics.track('Appointment Booking Succeeded', {
        appointment_category: appointment.appointmentType?.category?.name,
        appointment_type_id: appointment.appointmentType?.id,
        appointment_type_name: appointment.appointmentType?.name,
        appointment_id: appointment.id,
        lab_id: appointment.location?.lab?.id,
        clinic_id: appointment.location?.id
      });
    }

    this.updateDataAndUrl({
      ...this.data,
      purchasedProduct: { type: 'appointment', data: appointment },
      selectedStep: 'success'
    });
    // @ts-ignore
    this.history.replace(this.generateConfirmationLink());
  }

  setMembershipPurchaseSuccess(membership: Membership) {
    if (window.analytics) {
      window.analytics.track('Membership Succeeded');
    }

    this.updateDataAndUrl({
      ...this.data,
      selectedStep: 'success',
      purchasedProduct: { type: 'membership', data: membership }
    });
    // @ts-ignore
    this.history.replace(this.generateConfirmationLink());
  }

  setCreateEnterpriseMembershipSuccess() {
    if (window.analytics) {
      window.analytics.track('Enterprise Membership Succceeded');
    }

    this.updateDataAndUrl({
      ...this.data,
      selectedStep: 'success',
      purchasedProduct: { type: 'enterprise_membership', data: {} }
    });
    // @ts-ignore
    this.history.replace(this.generateConfirmationLink());
  }

  skipToSignUp() {
    if (window.analytics) {
      window.analytics.track('Skip to Sign Up');
    }

    this.updateDataAndUrl({
      ...this.data,
      // @ts-ignore
      steps: { create_account: CreateAccountStep },
      selectedStep: 'create_account'
    });
  }
}

export function generateRescheduleLink(appointmentId: string) {
  return `/book?step=location&reschedule_appointment_id=${appointmentId}`;
}
